import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import CashinCashbackIcon from '../../../images/cashback/icon_cashback.svg'

import { StyledProgress, StyledVaultCounter } from './styles'
import { useTranslation } from '../../../context/translationProvider'
import { themeStyles } from '../../../styles/theme'
import { AuthContext } from '../../../context/authProvider'
import { Button } from '../../atoms/button'
import { FormatAmount } from '../../atoms/formatAmount'
import { If } from '../../atoms/if'
import { Image } from '../../atoms/image'
import { Timer } from '../../atoms/timer'
import { Text, Title } from '../../atoms/text'
import { pushDataLayer } from '../../../utils/pushDataLayer'

export const CashbackProgress = ({ toggleCashbackDrawer, gameEventData }) => {
  const { translate } = useTranslation()
  const { user, vaultAmount, shorterDeadline, cashout, loadingCashout } =
    useContext(AuthContext)
  const [timerExpired, setTimerExpired] = useState(false)
  const [isCashoutDisabled, setIsCashoutDisabled] = useState(true)

  const onFinish = () => {
    setIsCashoutDisabled(true)
    setTimerExpired(true)
  }

  useEffect(() => {
    if (!timerExpired) {
      setIsCashoutDisabled(vaultAmount === 0)
    }
  }, [vaultAmount, timerExpired])

  const handleCashbackDrawerToggle = () => {
    pushDataLayer({
      eventName: 'cashback_list-opened',
      data: gameEventData,
    })
    toggleCashbackDrawer()
  }

  const handleCashout = () => {
    pushDataLayer({
      eventName: 'cashout-done',
      data: gameEventData,
    })
    cashout()
  }

  return (
    <StyledVaultCounter>
      <div>
        <If
          condition={vaultAmount !== 0}
          render={() => (
            <>
              <StyledProgress>
                <CircularProgressbar
                  minValue={0}
                  maxValue={100}
                  strokeWidth={10}
                  value={((vaultAmount % 1) * 100).toFixed(0)}
                  styles={buildStyles({
                    pathTransitionDuration: 1,
                    pathColor: themeStyles.colors.accentColors.secondary.main,
                    trailColor: themeStyles.colors.dominantGrey.main,
                    strokeLinecap: 'butt',
                  })}
                />
              </StyledProgress>
              <Title
                bold
                textAlign="center"
                theme="info"
                extraBig
                noMargin
                tagType="span"
                level={1}
                style={{
                  whiteSpace: 'nowrap',
                  color: themeStyles.colors.accentColors.secondary.main,
                  fontStyle: 'normal',
                  fontFamily: 'Inter',
                  fontSize: 45,
                }}
              >
                <FormatAmount
                  amount={vaultAmount}
                  shortCode={user?.wallet?.currency?.short_code || 'BRL'}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  currencySymbol={user?.wallet?.currency?.symbol}
                />
              </Title>
            </>
          )}
          renderElse={() => <Image url={CashinCashbackIcon} width={100} />}
        />
      </div>

      <If
        condition={vaultAmount !== 0}
        render={() => (
          <div>
            <Text textAlign="center" style={{ fontSize: 16 }}>
              {translate('vault.activeForMessage')}
            </Text>
            <Timer
              endDate={
                shorterDeadline
                  ? new Date(shorterDeadline?.date)
                  : new Date('1970-01-01')
              }
              onFinish={onFinish}
              expiryReminder={shorterDeadline?.expiryTime}
            />
          </div>
        )}
      />

      <div>
        <If
          condition={vaultAmount === 0}
          render={() => (
            <Text textAlign="center" style={{ marginBottom: '2.5em' }}>
              {translate('vault.subtitle')}
            </Text>
          )}
        />
        <Button
          disabled={isCashoutDisabled || loadingCashout}
          bordered={isCashoutDisabled || loadingCashout}
          style={{ margin: '.5rem 0', minWidth: '100%' }}
          onClick={handleCashout}
        >
          {`${translate('vault.cashout')} `}
          <If
            condition={vaultAmount !== 0}
            render={() => (
              <FormatAmount
                amount={vaultAmount}
                shortCode={user?.wallet?.currency?.short_code || 'BRL'}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
                currencySymbol={user?.wallet?.currency?.symbol}
              />
            )}
          />
        </Button>
        <If
          condition={vaultAmount !== 0}
          render={() => (
            <Text textAlign="center" small style={{ fontSize: 14 }}>
              {translate('vault.subtitle')}
            </Text>
          )}
        />
      </div>

      <If
        condition={vaultAmount !== 0}
        render={() => (
          <Text
            textAlign="center"
            theme="info"
            onClick={handleCashbackDrawerToggle}
            style={{ cursor: 'pointer' }}
          >
            {translate('vault.cashbackBreakdown')}
          </Text>
        )}
      />
    </StyledVaultCounter>
  )
}

CashbackProgress.defaultProps = {
  toggleCashbackDrawer: () => {},
}

CashbackProgress.propTypes = {
  toggleCashbackDrawer: PropTypes.func,
}
